import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useMemo } from 'react';
import { generatePath, useNavigate, useSearchParams } from 'react-router-dom';

import { usePartnersReadCampaigns, usePartnersReadOffers } from '@/api/hooks';
import { type PartnerOffer } from '@/api/routes/partners/partners.offers.api';
import Button from '@/components/Button';
import LoadErrorState from '@/components/LoadErrorState';
import { IconPlus } from '@/components/icon/tabler/IconPlus';
import OffersEmptyState from '@/domain/offers/components/OffersEmptyState';
import OffersListSkeleton from '@/domain/offers/components/OffersListSkeleton';
import OffersStatusTabs from '@/domain/offers/components/OffersStatusTabs';
import OffersTable from '@/domain/offers/components/OffersTable';
import { type OffersTab } from '@/domain/offers/types';
import { getCampaignByOffer, parseOffersTab, toOffersListParams } from '@/domain/offers/utils';
import { useTranslate } from '@/state/i18n';
import { ScreenRoutes } from '@/types/router';
import { radiusTokens } from '@/theme/themePrimitives';

/** Campaigns read to show "No ar até…" next to each offer; one page covers any account today. */
const CAMPAIGNS_FOR_OFFERS = { offset: 0, limit: 100 };

/** `/offers` (P18): the partner's offers by status. The tab and page live in the URL. */
const OffersList = () => {
  const t = useTranslate('domain.offers.OffersList');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = parseOffersTab(searchParams.get('status'));
  const page = Math.max(0, Number(searchParams.get('page')) || 0);

  const params = useMemo(() => toOffersListParams(tab, page), [tab, page]);
  const { data, isLoading, isError, refetch } = usePartnersReadOffers(params);
  const { data: campaigns } = usePartnersReadCampaigns(CAMPAIGNS_FOR_OFFERS);
  const campaignByOffer = useMemo(() => getCampaignByOffer(campaigns?.items ?? []), [campaigns]);

  const changeTab = (next: OffersTab) =>
    setSearchParams(next === 'all' ? {} : { status: next }, { replace: true });
  const changePage = (next: number) =>
    setSearchParams({ ...(tab === 'all' ? {} : { status: tab }), page: String(next) });
  const create = () => navigate(ScreenRoutes.OFFER_NEW);
  const open = (offer: PartnerOffer) =>
    navigate(generatePath(ScreenRoutes.OFFER_DETAIL, { offerId: offer.id }));

  const renderContent = () => {
    if (isLoading) return <OffersListSkeleton />;
    if (isError || !data) {
      return <LoadErrorState message={t('loadError')} onRetry={() => refetch()} />;
    }
    if (!data.items.length) {
      return <OffersEmptyState filtered={data.statusCounts.all > 0} onCreate={create} />;
    }
    return (
      <OffersTable
        offers={data.items}
        campaignByOffer={campaignByOffer}
        count={data.count}
        page={page}
        onPageChange={changePage}
        onOpen={open}
      />
    );
  };

  return (
    <Stack gap={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
        <Typography component="h1" variant="h2">
          {t('title')}
        </Typography>
        <Button variant="contained" startIcon={<IconPlus />} onClick={create}>
          {t('create')}
        </Button>
      </Stack>
      <OffersStatusTabs value={tab} counts={data?.statusCounts} onChange={changeTab} />
      <Paper variant="outlined" sx={sx.surface}>
        {renderContent()}
      </Paper>
      <Typography variant="caption" color="text.secondary">
        {t('footnote')}
      </Typography>
    </Stack>
  );
};

export default OffersList;

const sx = {
  surface: { borderRadius: radiusTokens.xxl, overflow: 'hidden' },
};
